import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import API_BASE_URL from "../config";
import { calendarDate } from "./crmApi";
import "./Crm.css";

const CURRENCY_SYMBOLS = {
  INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ", AUD: "A$", CAD: "C$", SGD: "S$",
};

/**
 * The public quote page a client opens from the shared link — no login.
 * Branded with the studio's logo, printable, and answerable: Accept moves the
 * studio's lead to Booked; either answer lands in the lead's timeline.
 */
function QuoteView() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [responding, setResponding] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/quote/${token}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Quote not found");
      setQuote(data.quote);
    } catch (e) {
      setError(e.message);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const respond = async (action) => {
    const verb = action === "accept" ? "accept" : "decline";
    if (!window.confirm(`Are you sure you want to ${verb} this quote?`)) return;
    setResponding(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/quote/${token}/respond`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Something went wrong");
      load();
    } catch (e) {
      window.alert(e.message);
    }
    setResponding(false);
  };

  if (error) {
    return (
      <div className="qv-page">
        <div className="qv-sheet">
          <div className="crm-empty">
            <span className="crm-empty-icon">🔍</span>
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="qv-page">
        <div className="qv-sheet"><div className="crm-loading">Loading quote…</div></div>
      </div>
    );
  }

  const sym = CURRENCY_SYMBOLS[quote.currency || "INR"] || "₹";
  const money = (n) => `${sym}${Math.round(Number(n) || 0).toLocaleString(quote.currency === "INR" || !quote.currency ? "en-IN" : "en-US")}`;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : null;
  // valid_until is a calendar date, not an instant: a quote is good until the
  // END of that day, and the day itself must not shift with the reader's clock.
  const fmtDay = (d) => {
    const day = calendarDate(d);
    return day ? day.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : null;
  };
  const subtotal = (quote.items || []).reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const validDay = calendarDate(quote.valid_until);
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const expired = !!validDay && validDay < startOfToday && quote.status === "sent";
  const studioAddress = [quote.address, quote.city, quote.state, quote.country].filter(Boolean).join(", ");

  return (
    <div className="qv-page">
      <div className="qv-sheet">
        <header className="qv-head">
          <div className="qv-studio">
            {quote.logo_url && <img className="qv-logo" src={quote.logo_url} alt={quote.studio_name} />}
            <div>
              <h1>{quote.studio_name}</h1>
              {studioAddress && <p>{studioAddress}</p>}
              <p>
                {quote.studio_phone && <span>📞 {quote.studio_phone}</span>}
                {quote.studio_phone && quote.studio_email && " · "}
                {quote.studio_email && <span>✉️ {quote.studio_email}</span>}
              </p>
              {quote.gstin && <p>GSTIN: {quote.gstin}</p>}
            </div>
          </div>
          <div className="qv-meta">
            <div className="qv-badge">QUOTATION</div>
            <p><strong>{quote.quote_number}</strong></p>
            <p>{fmtDate(quote.updated_at || quote.created_at)}</p>
            {quote.valid_until && (
              <p className={expired ? "qv-expired" : ""}>
                Valid until {fmtDay(quote.valid_until)}
              </p>
            )}
          </div>
        </header>

        <div className="qv-for">
          <span>Prepared for</span>
          <h2>{quote.client_name}</h2>
          {quote.title && <p>{quote.title}</p>}
        </div>

        <table className="qv-table">
          <thead>
            <tr><th>Item</th><th>Amount</th></tr>
          </thead>
          <tbody>
            {(quote.items || []).map((item, i) => (
              <tr key={i}>
                <td>{item.label}</td>
                <td>{money(item.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {Number(quote.discount) > 0 && (
              <>
                <tr className="qv-sub"><td>Subtotal</td><td>{money(subtotal)}</td></tr>
                <tr className="qv-sub"><td>Discount</td><td>− {money(quote.discount)}</td></tr>
              </>
            )}
            <tr className="qv-total"><td>Total</td><td>{money(quote.total)}</td></tr>
          </tfoot>
        </table>

        {quote.notes && (
          <div className="qv-notes">
            <h3>Notes</h3>
            <p>{quote.notes}</p>
          </div>
        )}

        {quote.status === "accepted" && (
          <div className="qv-status accepted">✅ You accepted this quote{quote.responded_at ? ` on ${fmtDate(quote.responded_at)}` : ""}. {quote.studio_name} will be in touch!</div>
        )}
        {quote.status === "declined" && (
          <div className="qv-status declined">This quote was declined. Changed your mind? Contact {quote.studio_name} for an updated offer.</div>
        )}
        {quote.status === "sent" && (
          <div className="qv-actions">
            <button className="crm-btn primary qv-accept" disabled={responding} onClick={() => respond("accept")}>
              ✅ Accept quote
            </button>
            <button className="crm-btn ghost" disabled={responding} onClick={() => respond("decline")}>
              Decline
            </button>
            <button className="crm-btn ghost qv-print" onClick={() => window.print()}>
              🖨 Print / PDF
            </button>
          </div>
        )}
        {quote.status !== "sent" && (
          <div className="qv-actions">
            <button className="crm-btn ghost qv-print" onClick={() => window.print()}>
              🖨 Print / PDF
            </button>
          </div>
        )}

        <footer className="qv-foot">
          Quote {quote.quote_number} · {quote.studio_name} · Powered by Photentra
        </footer>
      </div>
    </div>
  );
}

export default QuoteView;
